
import api from "@/api";
import {
  DocumentsHistory,
  DocumentVersion,
} from "@/api/dtos/DocumentsHistory.schema";
import { Operator } from "@/api/dtos/Operator.schema";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import CancelButton from "../buttons/CancelButton.vue";
import EditButton from "../buttons/EditButton.vue";
import EditFrame from "../EditFrame.vue";
import { showToast } from "@/utils/toastService";
import { getAxiosResponse } from "@/utils/errorUtils";
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  History,
} from "tiptap-vuetify";

@Component({
  components: {
    EditFrame,
    EditButton,
    CancelButton,
    TiptapVuetify,
  },
})
export default class Documents extends Vue {
  @Prop({ required: false })
  operator?: Operator;

  readonly headers: {
    text: string;
    value: string;
    align?: "start" | "center" | "end";
  }[] = [
    { text: "Versionsnummer", value: "version" },
    { text: "Datum", value: "date" },
    { text: "Uhrzeit", value: "time" },
    { text: "", value: "actions" },
  ];

  sendInfoEmail = false;

  dialogTitle = "Datenschutzvereinbarung";
  documentHistory: DocumentsHistory | null = null;

  activeDocClass: "GDPR" | "GDPRW" | "GTC" | null = null;

  showHistoryDialog = false;
  editorEditingAllowed = true;

  showEditorDialog = false;
  editorContent = "";

  get editorTitle(): string {
    switch (this.activeDocClass) {
      case "GDPR":
        return "Datenschutzvereinbarung";
      case "GDPRW":
        return "Datenschutz Widerrufserklärung";
      case "GTC":
        return "Allgemeine Geschäftsbedingungen";
      default:
        return "Inhalt";
    }
  }

  get isGlobal(): boolean {
    return !this.operator;
  }

  extensions = [
    History,
    Link,
    Underline,
    Strike,
    Italic,
    ListItem,
    BulletList,
    OrderedList,
    [Heading, { options: { levels: [1, 2, 3] } }],
    Bold,
  ];

  async mounted(): Promise<void> {
    if (this.operator) {
      this.sendInfoEmail =
        this.operator.documentSettings.sendMailOnDataprotectionChanged;
      this.documentHistory = await api.OperatorsApi.getOperatorDocumentsHistory(
        this.operator.uuid
      );
    } else {
      this.documentHistory = await api.AdminApi.getPlatformDocumentsHistory();
    }
  }

  get selectedHistory(): {
    md5hash: string;
    date: string;
    time: string;
    version: number;
  }[] {
    if (!this.documentHistory) return [];
    let documentHistory: DocumentVersion[] = [];

    switch (this.activeDocClass) {
      case "GDPR":
        documentHistory = this.documentHistory.gdpr;
        break;
      case "GDPRW":
        documentHistory = this.documentHistory.gdprw;
        break;
      case "GTC":
        documentHistory = this.documentHistory.gtc;
        break;
      default:
        return [];
    }

    return documentHistory.map((doc) => ({
      md5hash: doc.md5hash,
      date: new Date(doc.time).toLocaleDateString(),
      time: new Date(doc.time).toLocaleTimeString(),
      version: doc.version,
    }));
  }

  @Watch("sendInfoEmail")
  async onSendInfoEmailChanged(): Promise<void> {
    if (this.operator) {
      await api.OperatorsApi.updateOperatorDocumentSettings(this.operator.uuid, {
        sendMailOnDataprotectionChanged: this.sendInfoEmail,
        sendMailOnGtcChanged: this.operator.documentSettings.sendMailOnGtcChanged,
      });
    } else {
      // nothing
    }
  }

  openGdprHistory(): void {
    if (!this.documentHistory) return;
    this.dialogTitle = "Datenschutzvereinbarung";
    this.activeDocClass = "GDPR";
    this.showHistoryDialog = true;
  }

  openGdprwHistory(): void {
    if (!this.documentHistory) return;
    this.dialogTitle = "Widerrufserklärung";
    this.activeDocClass = "GDPRW";
    this.showHistoryDialog = true;
  }

  openGtcHistory(): void {
    if (!this.documentHistory) return;
    this.dialogTitle = "Allgemeine Geschäftsbedingungen";
    this.activeDocClass = "GTC";
    this.showHistoryDialog = true;
  }

  openEditor(doc: "GDPR" | "GDPRW" | "GTC", editorEditingAllowed = true): void {
    this.activeDocClass = doc;
    this.editorContent = "";
    this.showEditorDialog = true;
    this.editorEditingAllowed = editorEditingAllowed;
  }

  closeEditor(): void {
    this.showEditorDialog = false;
    this.editorContent = "";
  }

  async onEditorSave(): Promise<void> {
    switch (this.activeDocClass) {
      case "GDPR":
        await this.uploadGdpr();
        break;
      case "GDPRW":
        await this.uploadGdprw();
        break;
      case "GTC":
        await this.uploadGtc();
        break;
    }
    this.closeEditor();
  }

  async selectFile(): Promise<File> {
    const fileUploader: HTMLInputElement = this.$refs
      .fileInput as HTMLInputElement;
    return new Promise((resolve, reject) => {
      fileUploader.onchange = (e) => {
        fileUploader.onchange = null;
        if (!(e.target as any).files.length) {
          reject(new Error("didn't select file"));
        }
        resolve((e.target as any).files[0]);
        if (e.target) {
          (e.target as any).value = "";
        }
      };
      setTimeout(
        () => reject(new Error("didn't select file in time.")),
        100000
      );
      fileUploader.click();
    });
  }

  async editLatestDocument(doc: "GDPR" | "GDPRW" | "GTC"): Promise<void> {
    this.activeDocClass = doc;
    let text = "";
    switch (doc) {
      case "GDPR":
        if (this.operator) {
          text = await api.OperatorsApi.downloadLatestOperatorGdrp(
            this.operator.uuid
          );
        } else {
          text = await api.AdminApi.downloadLatestPlatformGdrp();
        }
        break;
      case "GDPRW":
        if (this.operator) {
          text = await api.OperatorsApi.downloadLatestOperatorGdrpw(
            this.operator.uuid
          );
        } else {
          text = await api.AdminApi.downloadLatestPlatformGdrpw();
        }
        break;
      case "GTC":
        if (this.operator) {
          text = await api.OperatorsApi.downloadLatestOperatorGtc(
            this.operator.uuid
          );
        } else {
          text = await api.AdminApi.downloadLatestPlatformGtc();
        }
        break;
    }
    this.openEditor(this.activeDocClass!);
    this.editorContent = text;
  }

  async editDocument(document: DocumentVersion): Promise<void> {
    let text = "";
    switch (this.activeDocClass) {
      case "GDPR":
        if (this.operator) {
          text = await api.OperatorsApi.downloadOperatorGdrp(
            this.operator.uuid,
            document.version.toString()
          );
        } else {
          text = await api.AdminApi.downloadPlatformGdrp(
            document.version.toString()
          );
        }
        break;
      case "GDPRW":
        if (this.operator) {
          text = await api.OperatorsApi.downloadOperatorGdprw(
            this.operator.uuid,
            document.version.toString()
          );
        } else {
          text = await api.AdminApi.downloadPlatformGdprw(
            document.version.toString()
          );
        }
        break;
      case "GTC":
        if (this.operator) {
          text = await api.OperatorsApi.downloadOperatorGtc(
            this.operator.uuid,
            document.version.toString()
          );
        } else {
          text = await api.AdminApi.downloadPlatformGtc(
            document.version.toString()
          );
        }
        break;
    }
    this.openEditor(this.activeDocClass!, false);
    this.editorContent = text;
  }

  async uploadGdpr(): Promise<void> {
    try {
      if (this.operator) {
        await api.OperatorsApi.uploadOperatorGdrp(
          this.operator.uuid,
          this.editorContent
        );
      } else {
        await api.AdminApi.uploadPlatformGdrp(this.editorContent);
      }

      showToast({
        message: "GDPR-Dokument erfolgreich hochgeladen",
        type: "success",
      });

      if (this.operator) {
        this.documentHistory =
          await api.OperatorsApi.getOperatorDocumentsHistory(
            this.operator.uuid
          );
      } else {
        this.documentHistory =
          await api.AdminApi.getPlatformDocumentsHistory();
      }
      this.openGdprHistory();
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: "Fehler beim Hochladen des GDPR-Dokuments",
        type: "error",
      });
      console.error("GDPR upload error:", e);
    }
  }

  async uploadGdprw(): Promise<void> {
    try {
      if (this.operator) {
        await api.OperatorsApi.uploadOperatorGdprw(
          this.operator.uuid,
          this.editorContent
        );
      } else {
        await api.AdminApi.uploadPlatformGdprw(this.editorContent);
      }

      showToast({
        message: "GDPR-W-Dokument erfolgreich hochgeladen",
        type: "success",
      });

      if (this.operator) {
        this.documentHistory =
          await api.OperatorsApi.getOperatorDocumentsHistory(
            this.operator.uuid
          );
      } else {
        this.documentHistory =
          await api.AdminApi.getPlatformDocumentsHistory();
      }
      this.openGdprwHistory();
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: "Fehler beim Hochladen des GDPR-W-Dokuments",
        type: "error",
      });
      console.error("GDPR-W upload error:", e);
    }
  }

  async uploadGtc(): Promise<void> {
    try {
      if (this.operator) {
        await api.OperatorsApi.uploadOperatorGtc(
          this.operator.uuid,
          this.editorContent
        );
      } else {
        await api.AdminApi.uploadPlatformGtc(this.editorContent);
      }

      showToast({
        message: "AGB erfolgreich hochgeladen",
        type: "success",
      });

      if (this.operator) {
        this.documentHistory =
          await api.OperatorsApi.getOperatorDocumentsHistory(
            this.operator.uuid
          );
      } else {
        this.documentHistory =
          await api.AdminApi.getPlatformDocumentsHistory();
      }
      this.openGtcHistory();
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: "Fehler beim Hochladen der AGB",
        type: "error",
      });
      console.error("AGB upload error:", e);
    }
  }
}
